'use client';

import { Controller, useFormContext } from 'react-hook-form';
import { BadgeSelect } from '@/app/(main)/clients/create/components/BadgeSelect';
import RadioButton from '@/app/components/buttons/RadioButton';
import { TextArea } from '@/app/components/form/TextArea';
import {
  SEAT_PREFERENCES,
  CLASS_PREFERENCE_OPTIONS,
  type ClassPreference,
} from '@/types/clientsApiTypes';
import type { FlightPreferencesFormValues } from './types';

export function EditFlightPreferencesForm() {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FlightPreferencesFormValues>();

  const flyPrivateAviation = watch('fly_private_aviation');
  const seat = watch('seat');
  const isPrivate = flyPrivateAviation === 'yes';
  const isCommercial = flyPrivateAviation === 'no';

  return (
    <div>
      <div className="flex items-center gap-6 mb-4">
        <RadioButton
          label="Commercial"
          checked={flyPrivateAviation === 'no'}
          onChange={() => setValue('fly_private_aviation', 'no', { shouldValidate: true })}
        />
        <RadioButton
          label="Private aviation"
          checked={flyPrivateAviation === 'yes'}
          onChange={() => setValue('fly_private_aviation', 'yes', { shouldValidate: true })}
        />
      </div>

      {isPrivate && (
        <TextArea
          {...register('preferred_private_aviation_company')}
          disableResize
          label="Preferred private aviation company"
          placeholder="Please provide any relevant information we should know about your private aviation preferences."
          rows={4}
          errors={errors}
        />
      )}

      {isCommercial && (
        <>
          <p className="text-body-small text-blue-900 mt-4 mb-3">Seat Preference</p>
          <div className="flex items-center gap-6 mb-4">
            <RadioButton
              label="Window"
              checked={seat === SEAT_PREFERENCES.window}
              onChange={() => setValue('seat', SEAT_PREFERENCES.window, { shouldValidate: true })}
            />
            <RadioButton
              label="Aisle"
              checked={seat === SEAT_PREFERENCES.aisle}
              onChange={() => setValue('seat', SEAT_PREFERENCES.aisle, { shouldValidate: true })}
            />
          </div>
          <div>
            <Controller
              name="class_preference"
              control={control}
              render={({ field }) => (
                <BadgeSelect<ClassPreference>
                  label="Class Preference"
                  type="secondary"
                  hint="Select all that apply"
                  options={CLASS_PREFERENCE_OPTIONS}
                  value={(field.value as ClassPreference[]) ?? []}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}
